import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Input, Field, Loader, DatePicker } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSubtitle
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import countries from '@auth/components/countriesDropdown/farmersCountryList.json';
import { formatLocalDate } from '@applicationKYC/FarmersQuestionnaire/utilities/formatLocalDate';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import { DropdownInput } from '@applicationKYC/FarmersQuestionnaire/components/dropdownInput/dropdownInput';
import { identityDocumentTypeOptions } from '@applicationKYC/FarmersQuestionnaire/constants';
import { today } from '@/Constants/date';
import { selectKycAppState } from '@redux';

const defaultValues = {
  type: null,
  dateOfBirth: null,
  personalCode: null,
  documentNumber: null,
  citizenship: null,
  expiryDate: null,
  issuingCountry: null
};

export const FarmerDocumentPage = ({ onSubmit, isAtBottom, isInModal }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        type: Yup.string().required(t('validation_required_field')).nullable(),
        dateOfBirth: Yup.date()
          .max(today, t('validation_date_entered_must_be_no_further_than_today'))
          .required(t('validation_required_field'))
          .typeError(t('validation_date_is_not_valid'))
          .nullable(),
        personalCode: Yup.string().required(t('validation_required_field')).nullable(),
        documentNumber: Yup.string().required(t('validation_required_field')).nullable(),
        citizenship: Yup.string().required(t('validation_required_field')).nullable(),
        expiryDate: Yup.date()
          .min(today, t('validation_date_entered_must_be_future_date'))
          .required(t('validation_required_field'))
          .typeError(t('validation_date_is_not_valid'))
          .nullable(),
        issuingCountry: Yup.string().required(t('validation_required_field')).nullable()
      }),
    [t]
  );

  const existingValues = farmerInformation?.farmerSection?.farmersDocumentPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        const modifiedData = {
          ...data,
          dateOfBirth: formatLocalDate(data.dateOfBirth),
          expiryDate: formatLocalDate(data.expiryDate)
        };
        await onSubmit?.(modifiedData, 'farmersDocumentPage');
      },
      () => setTouchedOnAll
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_farmer_document_header')}</PageHeader>
        <PageSubtitle>{t('farmers_farmer_document_subtitle')}</PageSubtitle>
      </FarmersQuestionnaireHeader>
      <Field
        Component={RadioChipOption}
        options={identityDocumentTypeOptions}
        value={values.type}
        name="type"
        isInModal={isInModal}
        validationHelpers={validationHelpers}
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        name="personalCode"
        value={values.personalCode}
        placeholder={t('farmers_farmer_document_personalCode')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={DatePicker}
        name="dateOfBirth"
        required
        showDisabledMonthNavigation
        value={values.dateOfBirth?.toString()}
        placeholder={t('farmers_farmer_document_dateOfBirth')}
        validateOnBlur
        validateOnChange
        showYearDropdown
        yearDropdownItemNumber={99}
        scrollableYearDropdown
        validationHelpers={validationHelpers}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        type="number"
        name="documentNumber"
        value={values.documentNumber}
        placeholder={t('farmers_farmer_document_documentNumber')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <DropdownInput
        validationHelpers={validationHelpers}
        name="citizenship"
        value={values.citizenship}
        filterCountries={false}
        placeholder={t('farmers_farmer_document_citizenship')}
        options={countries}
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={DatePicker}
        name={'expiryDate'}
        required
        showDisabledMonthNavigation
        value={values.expiryDate?.toString()}
        placeholder={t('farmers_farmer_document_expiryDate')}
        validateOnBlur
        validateOnChange
        showYearDropdown
        yearDropdownItemNumber={99}
        scrollableYearDropdown
        validationHelpers={validationHelpers}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <DropdownInput
        validationHelpers={validationHelpers}
        name="issuingCountry"
        value={values.issuingCountry}
        filterCountries={false}
        placeholder={t('farmers_farmer_document_issuingCountry')}
        options={countries}
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />

      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary={true}
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
